// Implements: F-UIX-005
/**
 * How a collapsible panel is remembered between visits.
 *
 * Per-viewer UI state and nothing else: one key per panel,
 * `prototyper.panel.<id>`, whose value is the word `open` or `closed`. It holds
 * no identifier of a person, no credential and no server data, so it is not one
 * of the keys the browser-store audit forbids.
 *
 * Storage is a convenience, never a dependency. Both functions are wrapped in
 * `try/catch` because a private window, blocked site data or a full quota make
 * the accessor itself throw, and a panel that cannot remember must still open
 * and close.
 */

export const PANEL_KEY_PREFIX = "prototyper.panel.";

/** Lowercase words joined by hyphens: `gallery-trash`, never `ana@example.com`. */
const PANEL_ID_SHAPE = /^[a-z][a-z0-9-]{0,63}$/;

/** `true` or `false` when a state was remembered, `null` when none was (or storage is unusable). */
export function readPanelOpen(id: string): boolean | null {
  try {
    const stored = localStorage.getItem(`${PANEL_KEY_PREFIX}${id}`);
    if (stored === "open") return true;
    if (stored === "closed") return false;
    return null;
  } catch {
    return null;
  }
}

export function writePanelOpen(id: string, open: boolean): void {
  // A panel id names a SURFACE. Anything that does not look like one (an email,
  // a name, an identifier with a dot or a capital) is not written under a key
  // that would then sit in the browser.
  if (!PANEL_ID_SHAPE.test(id)) return;
  try {
    localStorage.setItem(`${PANEL_KEY_PREFIX}${id}`, open ? "open" : "closed");
  } catch {
    // Not remembering is the whole cost of a storage that refuses.
  }
}

/**
 * A resizable panel's width (UX-05: the editor's conversation column), in the same family: the
 * value is a whole number of pixels, nothing else. A stored value that is not one within
 * `min`..`max` reads as none.
 */
export function readPanelWidth(id: string, min: number, max: number): number | null {
  try {
    const stored = localStorage.getItem(`${PANEL_KEY_PREFIX}${id}`);
    if (stored === null || !/^\d{1,4}$/.test(stored)) return null;
    const width = Number(stored);
    return width >= min && width <= max ? width : null;
  } catch {
    return null;
  }
}

/** Remembers a width, or forgets it with `null` (back to the default). */
export function writePanelWidth(id: string, width: number | null): void {
  if (!PANEL_ID_SHAPE.test(id)) return;
  try {
    if (width === null) localStorage.removeItem(`${PANEL_KEY_PREFIX}${id}`);
    else localStorage.setItem(`${PANEL_KEY_PREFIX}${id}`, String(Math.round(width)));
  } catch {
    // Not remembering is the whole cost of a storage that refuses.
  }
}

/**
 * A remembered choice among a few words (UX-07: the gallery's `grid` or `list`), under the same
 * `prototyper.panel.<id>` family. Only a word the caller lists is read back; anything else reads as none.
 */
export function readPanelChoice<V extends string>(id: string, allowed: readonly V[]): V | null {
  try {
    const stored = localStorage.getItem(`${PANEL_KEY_PREFIX}${id}`);
    return allowed.find((word) => word === stored) ?? null;
  } catch {
    return null;
  }
}

export function writePanelChoice(id: string, choice: string): void {
  if (!PANEL_ID_SHAPE.test(id) || !PANEL_ID_SHAPE.test(choice)) return;
  try {
    localStorage.setItem(`${PANEL_KEY_PREFIX}${id}`, choice);
  } catch {
    // Not remembering is the whole cost of a storage that refuses.
  }
}
